/**
 * On someone else's profile: what you and they do together on Otable — the consortia you are both in
 * (with their proposals and publications) and shared labs. Hidden when there is nothing in common.
 */
import React from 'react';
import useSWR from 'swr';
import { Link as RouterLink } from 'react-router-dom';
import { Box, CardContent, Link, Paper, Stack, Typography } from '@mui/material';
import {
  Groups as ConsortiumIcon, Science as LabIcon, Description as ProposalIcon, MenuBook as PublicationIcon,
} from '@mui/icons-material';
import { fetcher } from 'shared/helpers/axios';

export default function TogetherCard({ userId, firstName }) {
  const { data } = useSWR(userId ? `/users/${userId}/together/` : null, fetcher, { revalidateOnFocus: false });
  const consortia = data?.consortia || [];
  const labs = data?.labs || [];
  if (!consortia.length && !labs.length) return null;

  return (
    <Paper sx={{ mt: 2, borderRadius: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 0.5 }}>You and {firstName || 'them'}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          What you work on together on Otable
        </Typography>
        <Stack spacing={1.25}>
          {consortia.map((c) => (
            <Box key={c.id} sx={{ p: 1.25, borderRadius: 1, bgcolor: 'action.hover' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <ConsortiumIcon color="primary" sx={{ fontSize: 18 }} />
                <Link component={RouterLink} to={`/consortium/${c.id}/`} underline="hover" color="text.primary"
                  sx={{ fontWeight: 700, fontSize: 14 }}>
                  {c.name}
                </Link>
              </Box>
              {c.documents.map((d) => {
                const Icon = d.type === 'publication' ? PublicationIcon : ProposalIcon;
                const path = d.type === 'publication' ? 'publications' : 'proposals';
                return (
                  <Box key={d.id} display="flex" alignItems="center" gap={0.75} sx={{ ml: 3.25, mt: 0.5 }}>
                    <Icon sx={{ fontSize: 15, color: 'text.secondary' }} />
                    <Link component={RouterLink} to={`/consortium/${c.id}/${path}/${d.id}/`} underline="hover"
                      color="text.secondary" sx={{ fontSize: 13 }} noWrap>
                      {d.title}
                    </Link>
                  </Box>
                );
              })}
              {c.document_count > c.documents.length && (
                <Typography sx={{ ml: 3.25, mt: 0.5, fontSize: 12.5, color: 'text.secondary' }}>
                  and {c.document_count - c.documents.length} more
                </Typography>
              )}
            </Box>
          ))}
          {labs.map((l) => (
            <Box key={l.id} display="flex" alignItems="center" gap={1} sx={{ p: 1.25, borderRadius: 1, bgcolor: 'action.hover' }}>
              <LabIcon color="primary" sx={{ fontSize: 18 }} />
              <Box minWidth={0}>
                <Link component={RouterLink} to={`/lab/${l.id}/`} underline="hover" color="text.primary" sx={{ fontWeight: 700, fontSize: 14 }}>
                  {l.name}
                </Link>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }} noWrap>{l.organization}</Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Paper>
  );
}
